import { useState, useCallback } from 'react';
import { DrillDownFornecedorData } from '@/Pages/AnalisesCompras/types';

type DrillLevel = 'list' | 'detail';

interface UseFornecedorDrillDownProps {
  spedFiscalId: number | null | undefined;
}

interface UseFornecedorDrillDownReturn {
  drillLevel: DrillLevel;
  selectedSupplier: string | null;
  drillDownData: DrillDownFornecedorData | null;
  loading: boolean;
  error: string | null;
  handleBarClick: (clickData: any) => Promise<void>;
  handleDrillUp: () => void;
  retry: () => Promise<void>;
  // Compatibilidade com DrillDownModal
  isModalOpen: boolean;
  data: DrillDownFornecedorData | null;
  selectedItem: { nome: string } | null;
  openModal: (supplier: { nome: string; cod_part?: string } | string) => Promise<void>;
  closeModal: () => void;
}

export const useFornecedorDrillDown = ({ 
  spedFiscalId 
}: UseFornecedorDrillDownProps): UseFornecedorDrillDownReturn => {
  const [drillLevel, setDrillLevel] = useState<DrillLevel>('list');
  const [selectedSupplier, setSelectedSupplier] = useState<string | null>(null);
  const [drillDownData, setDrillDownData] = useState<DrillDownFornecedorData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDrillDownData = useCallback(async (supplier: { nome: string; cod_part?: string } | string) => {
    if (spedFiscalId === null || spedFiscalId === undefined || spedFiscalId <= 0) {
      setError('ID fiscal inválido');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const supplierName = typeof supplier === 'string' ? supplier : supplier.nome;
      const codPart = typeof supplier === 'string' ? undefined : supplier.cod_part;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}sped-fiscal/fornecedor-detalhes-drill-down/${spedFiscalId}/${encodeURIComponent(supplierName)}${codPart ? `?cod_part=${encodeURIComponent(codPart)}` : ''}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: DrillDownFornecedorData = await response.json();

      if (data && typeof data === 'object') {
        setDrillDownData(data);
        setDrillLevel('detail');
      } else {
        throw new Error('Dados inválidos retornados do servidor');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      setError(errorMessage);
      console.error('Erro ao buscar detalhes do fornecedor:', err);
    } finally {
      setLoading(false);
    }
  }, [spedFiscalId]);

  const handleBarClick = useCallback(async (clickData: any) => {
    if (spedFiscalId === null || spedFiscalId === undefined || spedFiscalId <= 0) return;

    const supplierName = clickData.indexValue as string;
    const codPart = (clickData as any).cod_part;
    setSelectedSupplier(supplierName);
    await fetchDrillDownData({ nome: supplierName, cod_part: codPart });
  }, [spedFiscalId, fetchDrillDownData]);

  const handleDrillUp = useCallback(() => {
    setDrillLevel('list');
    setSelectedSupplier(null);
    setDrillDownData(null);
    setError(null);
  }, []);

  const retry = useCallback(async () => {
    if (selectedSupplier) {
      await fetchDrillDownData(selectedSupplier);
    }
  }, [selectedSupplier, fetchDrillDownData]);

  return {
    drillLevel,
    selectedSupplier,
    drillDownData,
    loading,
    error,
    handleBarClick,
    handleDrillUp,
    retry,
    // Compat: campos esperados pelo DrillDownModal
    isModalOpen: drillLevel === 'detail',
    data: drillDownData,
    selectedItem: selectedSupplier ? { nome: selectedSupplier } : null,
    openModal: (supplier: { nome: string; cod_part?: string } | string) =>
      fetchDrillDownData(
        typeof supplier === 'string'
          ? { nome: supplier }
          : supplier
      ),
    closeModal: handleDrillUp
  };
}; 